import { ApolloClient } from "@apollo/client";
import {
  Brand,
  BrandFeature,
  BrandPurchaser,
  BrandUser,
  type IBrandProps,
  type DBrandUser,
  type IBrandPurchaserRow,
} from "@src/domain/entities/brand";
import type {
  GetBrandsDTO,
  UpdateBrandDTO,
  UpdateBrandStatusDTO,
  UpdateBrandFeatureStatusDTO,
  UpdateBrandPurchaserStatusDTO,
  UpdateBrandUserStatusDTO,
} from "@src/domain/dto/brands";
import type {
  IBrandGetBrandsResult,
  IBrandGetDetailsResult,
  IBrandPaginatedResponse,
  IBrandRepository,
  IBrandDetailsResponse,
} from "@src/domain/repositories/brand";
import {
  GET_BRANDS,
  GET_BRAND_DETAILS,
  UPDATE_BRAND_STATUS,
  UPDATE_BRAND_FEATURE_STATUS,
  UPDATE_BRAND_PURCHASER_STATUS,
  UPDATE_BRAND_USER_STATUS,
} from "@src/domain/graphql";
import { AdminRestApiPaths } from "@src/infrastructure/adapters/http";
import { MultipartFormService } from "@src/infrastructure/services/multipartform";

/**
 * Brand repository – GraphQL reads; REST multipart writes (camelCase).
 */
export class BrandRepository implements IBrandRepository {
  private readonly client: ApolloClient;

  constructor(client: ApolloClient) {
    this.client = client;
  }

  /**
   * Evicts all cached brands data so the next query hits the network.
   * Use on refetch (force fresh data) or after mutations so UI gets up-to-date data.
   */
  private invalidateBrandsCache(): void {
    this.client.cache.evict({ id: "ROOT_QUERY", fieldName: "brands" });
  }

  /**
   * Fetches brands. When refetch is true: flush Apollo cache then fetch (guaranteed fresh).
   * When refetch is false: Apollo uses cache-first, so cached data is returned when available (better performance).
   */
  async getBrands(
    params: GetBrandsDTO,
    refetch?: boolean,
  ): Promise<IBrandGetBrandsResult> {
    if (refetch) {
      this.invalidateBrandsCache();
    }
    const response = await this.fetchBrandsFromApi(params);
    return { data: response };
  }

  private async fetchBrandsFromApi(
    params: GetBrandsDTO,
  ): Promise<IBrandPaginatedResponse> {
    const result = await this.client.query<IBrandPaginatedResponse>({
      query: GET_BRANDS,
      variables: {
        params: {
          page_number: params.page_number,
          page_size: params.page_size,
        },
      },
    });
    if (!result.data) throw new Error("No brands found");
    return result.data;
  }

  async getBrandDetails(brandId: number): Promise<IBrandGetDetailsResult> {
    const result = await this.client.query<IBrandDetailsResponse>({
      query: GET_BRAND_DETAILS,
      variables: { brandId },
    });
    if (!result.data) throw new Error("Brand details not found");
    return { data: result.data };
  }

  async updateBrandStatus(params: UpdateBrandStatusDTO): Promise<Brand> {
    const brand = await this.client.mutate<{ updateBrand: IBrandProps }>({
      mutation: UPDATE_BRAND_STATUS,
      variables: {
        params: {
          brandId: params.brandId,
          brandIsActive: params.brandIsActive,
        },
      },
    });
    if (!brand.data?.updateBrand) {
      throw new Error("Failed to update brand status");
    }
    this.invalidateBrandsCache();
    return new Brand(brand.data.updateBrand);
  }

  async updateBrandUserStatus(
    params: UpdateBrandUserStatusDTO,
  ): Promise<BrandUser> {
    const result = await this.client.mutate<{
      updateBrandUserStatus: DBrandUser;
    }>({
      mutation: UPDATE_BRAND_USER_STATUS,
      variables: {
        params: {
          brandId: params.brandId,
          brandUserId: params.brandUserId,
          isActive: params.isActive,
        },
      },
    });

    if (!result.data?.updateBrandUserStatus) {
      throw new Error("Failed to update brand user status");
    }

    this.client.cache.evict({ id: "ROOT_QUERY", fieldName: "brandDetails" });

    return new BrandUser(result.data.updateBrandUserStatus);
  }

  async updateBrandFeatureStatus(
    params: UpdateBrandFeatureStatusDTO,
  ): Promise<BrandFeature> {
    const result = await this.client.mutate<{
      updateBrandFeatureStatus: BrandFeature;
    }>({
      mutation: UPDATE_BRAND_FEATURE_STATUS,
      variables: { params },
    });
    if (!result.data?.updateBrandFeatureStatus) {
      throw new Error("Failed to update brand feature status");
    }
    this.client.cache.evict({ id: "ROOT_QUERY", fieldName: "brandDetails" });
    return new BrandFeature(result.data.updateBrandFeatureStatus);
  }

  async updateBrandPurchaserStatus(
    params: UpdateBrandPurchaserStatusDTO,
  ): Promise<BrandPurchaser> {
    const result = await this.client.mutate<{
      updateBrandPurchaserStatus: IBrandPurchaserRow;
    }>({
      mutation: UPDATE_BRAND_PURCHASER_STATUS,
      variables: { params },
    });
    if (!result.data?.updateBrandPurchaserStatus) {
      throw new Error("Failed to update brand purchaser status");
    }
    this.client.cache.evict({ id: "ROOT_QUERY", fieldName: "brandDetails" });
    return BrandPurchaser.from(result.data.updateBrandPurchaserStatus);
  }

  async updateBrand(params: UpdateBrandDTO): Promise<Brand> {
    const { data } = await new MultipartFormService().post<{
      data: IBrandProps;
    }>(AdminRestApiPaths.brand, params.toMultipartFormData());

    if (!data?.brandId) {
      throw new Error("Brand save failed");
    }

    this.invalidateBrandsCache();
    this.client.cache.evict({ id: "ROOT_QUERY", fieldName: "brandDetails" });
    this.client.cache.evict({
      id: "ROOT_QUERY",
      fieldName: "organizationDetails",
    });
    this.client.cache.evict({ id: "ROOT_QUERY", fieldName: "organizations" });
    return new Brand({
      ...data,
      brandFeatures: data.brandFeatures ?? null,
      brandUsers: data.brandUsers ?? null,
    });
  }
}
